// Template 10 — "Halo" — a soft, elegant premium light landing page: warm-white
// canvas, a framed rounded hero panel, Instrument Serif display, pastel glows,
// a syllabus-style module grid and soft gradient CTA panels. A distinct light
// layout (not a recolor of any other template). Fully theme-driven.
import { Fragment, useState } from "react";
import RichText from "../RichText";
import { Icon, Stars, splitTitle, rgba, shade, useGoogleFonts, usePreviewVideo, VideoModal, HeroVideo, SOCIAL_ICONS, orderedSections, skillIconD, PolicyLinks, GuaranteeBadge, Reveal, LessonIcon } from "./shared";

const SIGNATURE2 = "#FB7185";
const SIGNATURE = "#14B8A6";     // fresh teal-mint — soft on white
const BG = "#faf9fb";            // warm-white canvas
const PANEL = "#ffffff";
const SOFT = "#f3f2f6";          // alt fill
const INK = "#1a1a22";
const MUT = "#6b6b78";
const LINE = "#ececf1";
const DISPLAY = "'Instrument Serif', Georgia, serif";
const SANS = "'Inter', system-ui, sans-serif";

export default function Template10({ course, landing, instructor, modules, themeColor, themeColor2, canView, labels = {}, onEnroll, policies }) {
  useGoogleFonts("https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Inter:wght@400;500;600;700&display=swap");
  const vid = usePreviewVideo();
  const A = themeColor || SIGNATURE;
  const AD = shade(A, -0.16);
  const AT = shade(A, -0.16);       // legible-on-light accent text
  const B = themeColor2 || SIGNATURE2;   // secondary branding accent
  const grad = `linear-gradient(135deg, ${A}, ${AD})`;
  const pal = { A, AD, AT, B, grad };

  const header = landing.header || {};
  const t = splitTitle(header.headline || course.name);
  const sub = header.subheadline || course.short_description || "";
  const isPaid = course.access_type === 2;
  const priceLabel = isPaid && course.price > 0 ? course.price : (labels.free || "Free");
  const ctaLabel = isPaid ? (labels.buy_now || "Buy Now") : (labels.enroll_now || "Enroll Now");
  const statusLabel = isPaid ? "Premium course" : course.access_type === 3 ? (canView ? "Unlocked" : "Members only") : "Free course";
  const lessonCount = course.total_lessons || modules.reduce((a, m) => a + (m.lessons?.length || 0), 0);

  const order = orderedSections(landing.section_order);
  const navLinks = [
    landing.about?.show !== false && (landing.description || course.description) && { href: "#t10-about", label: "About" },
    landing.skill?.show && landing.skill.list?.length > 0 && { href: "#t10-skill", label: "Skills" },
    modules.length > 0 && landing.course_include?.show !== false && { href: "#t10-curriculum", label: labels.curriculum || "Curriculum" },
    instructor?.name && landing.instructor?.show !== false && { href: "#t10-instructor", label: "Instructor" },
    landing.testimonials?.show && landing.testimonials.list?.length > 0 && { href: "#t10-reviews", label: labels.reviews || "Reviews" },
    landing.faq?.show && landing.faq.list?.length > 0 && { href: "#t10-faq", label: labels.faq || "FAQ" },
  ].filter(Boolean);

  const stats = [
    course.star_rating > 0 && `${Number(course.star_rating).toFixed(1)} ${labels.rating || "Rating"}`,
    course.total_lessons > 0 && `${course.total_lessons} ${labels.lessons || "Lessons"}`,
    modules.length > 0 && `${modules.length} ${labels.modules || "Modules"}`,
    "Lifetime access",
  ].filter(Boolean);

  const blocks = {
    about: landing.about?.show !== false && (landing.description || course.description) && (
      <Reveal><Sec id="t10-about" eyebrow="Overview" title="About this course" pal={pal}>
        <div className="prose prose-sm mt-6 max-w-3xl leading-relaxed" style={{ color: MUT }}><RichText html={landing.description || course.description} /></div>
      </Sec></Reveal>
    ),
    instructor: landing.instructor?.show !== false && instructor?.name && (
      <Reveal><div id="t10-instructor"><Sec eyebrow="Instructor" title={labels.meet_instructor || "Meet the instructor"} pal={pal}>
        <div className="relative mt-8 overflow-hidden rounded-[2rem] border p-6 sm:p-8" style={{ background: PANEL, borderColor: LINE, boxShadow: "0 20px 50px -34px rgba(26,26,34,.25)" }}>
          <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full opacity-60 blur-3xl" style={{ background: rgba(A, 0.18) }} />
          <div className="relative grid items-center gap-8 sm:grid-cols-[210px_1fr]">
            <div className="relative mx-auto w-full max-w-[210px]">
              <div className="relative aspect-[4/5] overflow-hidden rounded-[1.5rem]" style={{ border: `1px solid ${LINE}`, boxShadow: `0 24px 50px -26px ${rgba(A, 0.4)}` }}>
                {instructor.profile_image ? <img src={instructor.profile_image} alt="" className="h-full w-full object-cover" /> : <span className="flex h-full w-full items-center justify-center text-6xl" style={{ background: SOFT, color: AT, fontFamily: DISPLAY }}>{instructor.name.charAt(0).toUpperCase()}</span>}
              </div>
              <span className="absolute -bottom-3 -right-3 flex h-12 w-12 items-center justify-center rounded-2xl text-white" style={{ background: grad, boxShadow: `0 12px 26px ${rgba(A, 0.4)}` }}><Icon d="M4.5 12.75l6 6 9-13.5" className="h-5 w-5" stroke={2.4} /></span>
            </div>
            <div className="text-center sm:text-left">
              <span className="text-[0.72rem] font-bold uppercase tracking-[0.16em]" style={{ color: AT }}>Course Instructor</span>
              <h3 className="mt-1.5 text-[2rem] leading-tight" style={{ fontFamily: DISPLAY, color: INK }}>{instructor.name}</h3>
              <span className="mx-auto mt-3 block h-1 w-12 rounded-full sm:mx-0" style={{ background: grad }} />
              {instructor.bio && <p className="mt-4 max-w-xl text-[15px] leading-relaxed" style={{ color: MUT }}>{instructor.bio}</p>}
            </div>
          </div>
        </div>
      </Sec></div></Reveal>
    ),
    skill: landing.skill?.show && landing.skill.list?.length > 0 && (
      <Reveal><Sec id="t10-skill" eyebrow={labels.what_youll_learn || "What you'll learn"} title={landing.skill.heading || "Skills you will walk away with"} pal={pal}>
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {landing.skill.list.map((s, i) => (
            <div key={i} className="group flex items-start gap-4 rounded-3xl border p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_22px_44px_-28px_rgba(26,26,34,.3)]" style={{ background: PANEL, borderColor: LINE }}>
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl text-white transition-transform duration-300 group-hover:scale-110" style={{ background: grad, boxShadow: `0 8px 20px ${rgba(A, 0.3)}` }}><Icon d={skillIconD(landing.skill.icon)} className="h-5 w-5" stroke={2.2} /></span>
              <span className="pt-1.5 text-[15px] leading-snug" style={{ color: INK }}>{s}</span>
            </div>
          ))}
        </div>
        {landing.skill.long_desc && <div className="rich-content mt-6 max-w-3xl text-[15px] leading-relaxed" style={{ color: MUT }} dangerouslySetInnerHTML={{ __html: landing.skill.long_desc }} />}
      </Sec></Reveal>
    ),
    include: modules.length > 0 && landing.course_include?.show !== false && (
      <Reveal><div id="t10-curriculum"><Sec eyebrow={labels.curriculum || "Curriculum"} title={labels.what_inside_course || "What's inside the course"} sub={`${modules.length} ${labels.modules || "Modules"} · ${lessonCount} ${labels.lessons || "Lessons"}`} pal={pal}>
        <Curriculum modules={modules} pal={pal} labels={labels} />
      </Sec></div></Reveal>
    ),
    testimonial: <div id="t10-reviews"><Testimonials testimonials={landing.testimonials} pal={pal} labels={labels} /></div>,
    moneyback: isPaid && <Guarantee guarantee={landing.guarantee} pal={pal} />,
    faq: <div id="t10-faq"><Faq faq={landing.faq} pal={pal} labels={labels} /></div>,
  };

  return (
    <div className="min-h-screen" style={{ background: BG, color: INK, fontFamily: SANS }}>
      {/* NAV */}
      <nav className="sticky top-0 z-50 border-b backdrop-blur-xl" style={{ background: rgba(BG, 0.8), borderColor: LINE }}>
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <a href="#top" className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-xl text-white" style={{ background: grad }}><Icon d="M12 14l9-5-9-5-9 5 9 5z M12 14l6.16-3.42a12 12 0 01.34 3.42c0 3-3 5-6.5 5S5.5 17 5.5 14a12 12 0 01.34-3.42L12 14z" className="h-4 w-4" /></span>
            <span className="max-w-[40vw] truncate text-[1.15rem] sm:max-w-xs" style={{ fontFamily: DISPLAY }}>{course.name}</span>
          </a>
          <div className="hidden items-center gap-7 md:flex">
            {navLinks.map((l) => <a key={l.href} href={l.href} className="text-sm font-semibold transition hover:text-gray-900" style={{ color: MUT }}>{l.label}</a>)}
          </div>
          <button onClick={onEnroll} className="rounded-full px-5 py-2 text-sm font-bold text-white transition hover:-translate-y-0.5" style={{ background: grad, boxShadow: `0 6px 18px ${rgba(A, 0.35)}` }}>{ctaLabel}</button>
        </div>
      </nav>

      {/* HERO — framed panel */}
      <header id="top" className="px-3 pt-6 sm:px-6 sm:pt-8 lg:px-8">
        <div className="relative mx-auto max-w-6xl overflow-hidden rounded-[2.5rem] border" style={{ borderColor: LINE, background: `linear-gradient(155deg, ${rgba(A, 0.1)}, ${PANEL} 60%)` }}>
          <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full opacity-60 blur-[90px]" style={{ background: rgba(A, 0.22) }} />
          <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full opacity-50 blur-[90px]" style={{ background: rgba(B, 0.18) }} />
          {header.bg_image ? <img src={header.bg_image} alt="" className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.08]" /> : null}
          <div className="relative grid items-center gap-10 p-7 sm:p-12 lg:grid-cols-[1.05fr_.95fr] lg:gap-12 lg:p-16">
            <div>
              <span className="inline-flex animate-fade-in-up items-center gap-2 rounded-full px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-wider" style={{ background: rgba(A, 0.12), color: AT }}>
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: A }} />{statusLabel}
              </span>
              <h1 className="mt-5 animate-fade-in-up text-[3rem] leading-[0.98] sm:text-6xl lg:text-[4.4rem]" style={{ fontFamily: DISPLAY, animationDelay: "60ms" }}>
                {t.before} <em style={{ fontStyle: "italic", color: A }}>{t.accent}</em> {t.after}
              </h1>
              {sub && <p className="mt-6 max-w-lg animate-fade-in-up text-[16px] leading-relaxed" style={{ color: MUT, animationDelay: "120ms" }}>{sub}</p>}
              <div className="mt-8 flex animate-fade-in-up flex-wrap items-center gap-3" style={{ animationDelay: "180ms" }}>
                <button onClick={onEnroll} className="group inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-[15px] font-bold text-white transition-transform duration-200 hover:-translate-y-0.5" style={{ background: grad, boxShadow: `0 14px 34px ${rgba(A, 0.4)}` }}>{ctaLabel} <Icon d="M13 7l5 5m0 0l-5 5m5-5H6" className="h-4 w-4 transition-transform group-hover:translate-x-1" /></button>
                <a href="#t10-curriculum" className="inline-flex items-center gap-2 rounded-full border bg-white/60 px-6 py-3.5 text-[15px] font-semibold transition hover:bg-white" style={{ borderColor: LINE, color: INK }}>{labels.preview || "Preview Course"}</a>
              </div>
              <div className="mt-8 flex animate-fade-in-up flex-wrap items-center gap-x-5 gap-y-2 text-[13px]" style={{ color: MUT, animationDelay: "240ms" }}>
                {stats.map((s, i) => (
                  <span key={i} className="inline-flex items-center gap-5">
                    {i > 0 && <span className="h-3 w-px" style={{ background: LINE }} />}
                    {i === 0 && course.star_rating > 0 ? <span className="inline-flex items-center gap-1.5"><Stars value={course.star_rating} color={A} empty="#e5e5ec" /> <strong style={{ color: INK }}>{s}</strong></span> : <strong style={{ color: INK, fontWeight: 600 }}>{s}</strong>}
                  </span>
                ))}
              </div>
            </div>

            {landing.header?.show !== false && (
              <div className="relative animate-fade-in-up" style={{ animationDelay: "160ms" }}>
                <div className="overflow-hidden rounded-[1.75rem] border bg-white p-2 shadow-[0_36px_70px_-38px_rgba(26,26,34,.4)]" style={{ borderColor: LINE }}>
                  <div className="overflow-hidden rounded-[1.35rem]">
                    <HeroVideo video={landing.video} poster={course.course_image} accent={A} className="aspect-video w-full" style={{ background: "#0e0e12" }} onExpand={vid.play} premium />
                  </div>
                </div>
                {course.total_lessons > 0 && (
                  <div className="absolute -bottom-4 -left-4 hidden items-center gap-2 rounded-2xl border bg-white px-3.5 py-2.5 shadow-lg sm:flex" style={{ borderColor: LINE }}>
                    <span className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ background: rgba(A, 0.12), color: AT }}><Icon d="M12 6.5C10.5 5.5 8 5 6 5v11c2 0 4.5.5 6 1.5m0-12c1.5-1 4-1.5 6-1.5v11c-2 0-4.5.5-6 1.5m0-12v12" className="h-4 w-4" /></span>
                    <div className="leading-tight"><div className="text-lg" style={{ fontFamily: DISPLAY, color: INK }}>{course.total_lessons}</div><div className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: MUT }}>{labels.lessons || "Lessons"}</div></div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* BODY */}
      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        {/* ENROLL PANEL */}
        <Reveal>
          <div className="mb-20 overflow-hidden rounded-[2rem] border bg-white shadow-[0_30px_70px_-46px_rgba(26,26,34,.4)]" style={{ borderColor: LINE }}>
            <div className="grid gap-6 p-6 sm:grid-cols-[auto_1fr_auto] sm:items-center sm:gap-9 sm:p-8">
              <div className="text-center sm:border-r sm:pr-9 sm:text-left" style={{ borderColor: LINE }}>
                <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: AT }}>{statusLabel}</span>
                <div className="mt-1 text-[3.2rem] leading-none" style={{ fontFamily: DISPLAY, color: INK }}>{priceLabel}{isPaid && <span className="ml-2 align-middle text-sm text-gray-400" style={{ fontFamily: SANS }}>one-time</span>}</div>
              </div>
              <ul className="flex flex-wrap justify-center gap-x-6 gap-y-2.5">
                {[course.total_lessons > 0 && `${course.total_lessons} ${labels.lessons || "lessons"}`, modules.length > 0 && `${modules.length} ${labels.modules || "modules"}`, "Lifetime access", "Learn on any device"].filter(Boolean).map((it, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm font-medium" style={{ color: MUT }}><Icon d="M4.5 12.75l6 6 9-13.5" className="h-4 w-4" stroke={2.4} style={{ color: A }} />{it}</li>
                ))}
              </ul>
              <button onClick={onEnroll} className="group flex items-center justify-center gap-2 rounded-full px-8 py-4 text-sm font-bold text-white transition-transform duration-200 hover:-translate-y-0.5" style={{ background: grad, boxShadow: `0 14px 32px ${rgba(A, 0.38)}` }}>{ctaLabel} <Icon d="M13 7l5 5m0 0l-5 5m5-5H6" className="h-4 w-4 transition-transform group-hover:translate-x-1" /></button>
            </div>
            {isPaid && landing.guarantee?.show && (
              <div className="border-t px-6 py-3.5 text-center text-[13px] sm:px-8" style={{ borderColor: LINE, background: rgba(A, 0.05) }}>
                <span className="font-bold" style={{ color: INK }}>{landing.guarantee.days || 30}-Day {labels.money_back_guarantee || "Money-Back Guarantee"}.</span> <span style={{ color: MUT }}>{labels.not_satisfied || "Not satisfied? Get a full refund."}</span>
              </div>
            )}
          </div>
        </Reveal>

        {/* SECTIONS — full width */}
        <div className="space-y-24">
          {order.map((id) => <Fragment key={id}>{blocks[id]}</Fragment>)}
        </div>

        {/* FINAL CTA — soft panel */}
        {landing.cta?.show !== false && (
          <Reveal>
            <section className="relative mt-20 overflow-hidden rounded-[2.5rem] border px-6 py-20 text-center sm:px-12" style={{ background: `linear-gradient(145deg, ${rgba(A, 0.14)}, ${PANEL} 65%)`, borderColor: LINE }}>
              <div className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full opacity-70 blur-[90px]" style={{ background: rgba(A, 0.22) }} />
              <div className="pointer-events-none absolute -bottom-24 -left-16 h-64 w-64 rounded-full opacity-50 blur-[90px]" style={{ background: rgba(AD, 0.16) }} />
              <div className="relative">
                <h2 className="text-[2.6rem] leading-tight sm:text-[3.6rem]" style={{ fontFamily: DISPLAY, color: INK }}>
                  {landing.cta?.heading ? landing.cta.heading : <>{t.before} <em style={{ fontStyle: "italic", color: A }}>{t.accent}</em> {t.after}</>}
                </h2>
                <p className="mx-auto mt-4 max-w-xl text-[15px] leading-relaxed" style={{ color: MUT }}>{landing.cta?.subheading || sub || `Join ${course.name} and start learning today.`}</p>
                <button onClick={onEnroll} className="group mt-8 inline-flex items-center gap-2 rounded-full px-8 py-4 text-[15px] font-bold text-white transition-transform duration-200 hover:-translate-y-0.5" style={{ background: grad, boxShadow: `0 16px 38px ${rgba(A, 0.45)}` }}>{ctaLabel} <Icon d="M13 7l5 5m0 0l-5 5m5-5H6" className="h-4 w-4 transition-transform group-hover:translate-x-1" /></button>
              </div>
            </section>
          </Reveal>
        )}
      </div>

      {/* FOOTER */}
      <footer className="border-t bg-white" style={{ borderColor: LINE }}>
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-5 px-4 py-10 sm:flex-row sm:px-6 lg:px-8">
          <div className="flex items-center gap-2.5">
            {Object.keys(SOCIAL_ICONS).filter((k) => landing.social?.[k]).map((k) => (
              <a key={k} href={landing.social[k]} target="_blank" rel="noopener noreferrer" aria-label={k} className="flex h-9 w-9 items-center justify-center rounded-full transition hover:-translate-y-0.5 hover:text-white" style={{ background: rgba(A, 0.09), color: AT }} onMouseEnter={(e) => { e.currentTarget.style.background = A; e.currentTarget.style.color = "#fff"; }} onMouseLeave={(e) => { e.currentTarget.style.background = rgba(A, 0.09); e.currentTarget.style.color = AT; }}>
                <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current"><path d={SOCIAL_ICONS[k]} /></svg>
              </a>
            ))}
          </div>
          <PolicyLinks policies={policies} accent={AT} />
        </div>
      </footer>

      <VideoModal video={landing.video} open={vid.open} onClose={vid.close} />
    </div>
  );
}

/* -------------------------------- helpers -------------------------------- */
function guaranteeDefaultHtml(days) {
  return `<p>We believe in the value of this course and are confident it will help you achieve your goals. That's why we offer a full ${days}-day risk-free money-back guarantee.</p><p>Go through the content, explore the lessons, and put what you learn into action. If at any point within the first ${days} days you feel the course isn't right for you or doesn't meet your expectations, simply reach out to us.</p><p>We'll process your refund promptly—no questions asked.</p><p>There's absolutely no risk in getting started today, only the opportunity to learn, grow, and succeed with complete peace of mind.</p>`;
}

function Sec({ id, eyebrow, title, sub, pal, children }) {
  return (
    <section id={id} className="scroll-mt-24">
      <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider" style={{ background: rgba(pal.A, 0.1), color: pal.AT }}><span className="h-1.5 w-1.5 rounded-full" style={{ background: pal.B }} />{eyebrow}</span>
      <h2 className="mt-4 text-[2.4rem] leading-[1.02] sm:text-[3.2rem]" style={{ fontFamily: DISPLAY, color: INK }}>{title}</h2>
      {sub && <p className="mt-2 text-[15px]" style={{ color: MUT }}>{sub}</p>}
      {children}
    </section>
  );
}

function Curriculum({ modules, pal, labels }) {
  return (
    <div className="mt-8 grid gap-5 sm:grid-cols-2">
      {modules.map((m, mi) => (
        <div key={mi} className="rounded-3xl border p-6 transition-all duration-300 hover:shadow-[0_24px_50px_-32px_rgba(26,26,34,.3)]" style={{ background: PANEL, borderColor: LINE }}>
          <div className="flex items-center gap-3.5">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl text-sm font-bold text-white" style={{ background: pal.grad, boxShadow: `0 8px 20px ${rgba(pal.A, 0.3)}` }}>{String(mi + 1).padStart(2, "0")}</span>
            <div className="min-w-0">
              <h3 className="truncate text-[1.25rem] leading-tight" style={{ fontFamily: DISPLAY, color: INK }}>{m.name}</h3>
              <span className="text-xs font-medium" style={{ color: MUT }}>{m.lessons?.length || 0} {labels.lessons || "lessons"}</span>
            </div>
          </div>
          {(m.lessons || []).length > 0 && (
            <ul className="mt-5 space-y-1 border-t pt-4" style={{ borderColor: LINE }}>
              {m.lessons.map((l, li) => (
                <li key={li} className="flex items-center gap-2.5 rounded-xl px-2 py-2 text-sm transition hover:bg-black/[.02]">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg" style={{ background: l.locked ? SOFT : rgba(pal.A, 0.1) }}>
                    <LessonIcon lesson={l} color={pal.AT} muted="#c3c3cc" className="h-3 w-3" />
                  </span>
                  <span className={`flex-1 truncate ${l.locked ? "text-gray-400" : "text-gray-600"}`}>{l.title}</span>
                  {l.completed && <span className="text-xs font-semibold" style={{ color: pal.AT }}>Done</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}

function Testimonials({ testimonials, pal, labels }) {
  const list = (testimonials?.list || []).filter((t) => t.quote || t.name);
  if (!testimonials?.show || !list.length) return null;
  return (
    <Reveal>
      <Sec eyebrow={labels.testimonials || "Testimonials"} title={testimonials.heading || "What students are saying"} pal={pal}>
        <div className="mt-8 grid gap-5 sm:grid-cols-2">
          {list.map((t, i) => (
            <figure key={i} className="flex flex-col rounded-[1.75rem] border p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_26px_50px_-30px_rgba(26,26,34,.3)]" style={{ background: PANEL, borderColor: LINE }}>
              <span className="text-6xl leading-none" aria-hidden style={{ fontFamily: DISPLAY, color: rgba(pal.A, 0.35) }}>&ldquo;</span>
              <Stars value={t.rating} empty="#e5e5ec" color={pal.A} className="-mt-2 h-4 w-4" />
              <blockquote className="mt-4 flex-1 text-[1.05rem] leading-relaxed" style={{ fontFamily: DISPLAY, color: INK }}>{t.quote}</blockquote>
              <figcaption className="mt-6 flex items-center gap-3 border-t pt-5" style={{ borderColor: LINE }}>
                <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full text-sm font-bold text-white" style={{ background: pal.grad }}>{t.avatar ? <img src={t.avatar} alt="" className="h-full w-full object-cover" /> : (t.name || "?").charAt(0).toUpperCase()}</span>
                <span className="min-w-0"><span className="block truncate text-sm font-bold" style={{ color: INK }}>{t.name}</span>{t.role && <span className="block truncate text-xs" style={{ color: MUT }}>{t.role}</span>}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </Sec>
    </Reveal>
  );
}

function Guarantee({ guarantee, pal }) {
  if (!guarantee?.show) return null;
  const days = guarantee.days || 30;
  return (
    <Reveal>
      <section className="relative overflow-hidden rounded-[2rem] border px-6 py-11 sm:px-10" style={{ background: `linear-gradient(135deg, ${rgba(pal.A, 0.14)}, ${SOFT})`, borderColor: LINE }}>
        <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full opacity-60 blur-3xl" style={{ background: rgba(pal.A, 0.22) }} />
        {guarantee.bg_image ? <img src={guarantee.bg_image} alt="" className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-15" /> : null}
        <div className="relative flex flex-col items-center gap-8 text-center sm:flex-row sm:text-left">
          <div className="order-2 flex-1 sm:order-1">
            <h3 className="text-[1.9rem] leading-tight sm:text-[2.2rem]" style={{ fontFamily: DISPLAY, color: INK }}>{guarantee.heading || `${days}-Day Risk-Free Money-Back Guarantee`}</h3>
            <div className="rich-content mt-3 text-[15px] leading-relaxed" style={{ color: MUT }} dangerouslySetInnerHTML={{ __html: guarantee.description || guaranteeDefaultHtml(days) }} />
          </div>
          <div className="order-1 shrink-0 sm:order-2">
            {guarantee.image
              ? <img src={guarantee.image} alt="" className="h-44 w-44 object-contain drop-shadow-xl" />
              : <GuaranteeBadge variant={guarantee.badge || 1} days={days} accent={pal.A} className="h-44 w-44 drop-shadow-xl sm:h-48 sm:w-48" />}
          </div>
        </div>
      </section>
    </Reveal>
  );
}

function Faq({ faq, pal, labels }) {
  const list = (faq?.list || []).filter((f) => f.q);
  const [open, setOpen] = useState(-1);
  if (!faq?.show || !list.length) return null;
  return (
    <Reveal>
      <Sec eyebrow={labels.faq || "FAQ"} title={faq.heading || "Frequently asked questions"} pal={pal}>
        <div className="mt-8 space-y-3">
          {list.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={i} className="overflow-hidden rounded-3xl border bg-white transition-all duration-300" style={isOpen ? { borderColor: rgba(pal.A, 0.4), boxShadow: `0 18px 40px -28px ${rgba(pal.A, 0.4)}` } : { borderColor: LINE }}>
                <button onClick={() => setOpen(isOpen ? -1 : i)} className="flex w-full items-center gap-4 px-6 py-5 text-left">
                  <span className="flex-1 text-[1.15rem]" style={{ fontFamily: DISPLAY, color: INK }}>{f.q}</span>
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition" style={isOpen ? { background: pal.grad } : { background: rgba(pal.B, 0.14) }}>
                    <Icon d={isOpen ? "M5 12h14" : "M12 5v14m7-7H5"} className="h-4 w-4" style={{ color: isOpen ? "#fff" : shade(pal.B, -0.18) }} />
                  </span>
                </button>
                <div className="grid transition-all duration-300" style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}>
                  <div className="overflow-hidden"><p className="px-6 pb-5 text-[15px] leading-relaxed" style={{ color: MUT }}>{f.a}</p></div>
                </div>
              </div>
            );
          })}
        </div>
      </Sec>
    </Reveal>
  );
}
